import {
  type FocusEvent,
  type FocusEventHandler,
  type RefObject,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react'

import {useBoardFocusGrid, useBoardFocusStore} from './contexts'
import {isSamePosition} from './focus-reducer'
import type {BoardFocusPosition} from './types'

/**
 * What one element needs to know about the board's focus, as a single value.
 *
 * A number rather than the position itself, and that is the point: every card and cell on
 * the board subscribes to this, and a move has to wake only the two whose answer changed.
 * A snapshot that returned an object would compare unequal every time and wake all of
 * them, which is the cost this whole shape exists to avoid.
 */
const NOT_ROVING = 0
const ROVING = 1
const ROVING_AND_OWED_FOCUS = 2

export type RovingFocusOptions = {
  /**
   * Whether focus landing anywhere inside this element counts as focus on it.
   *
   * True for a card, because a caller draws whatever it likes inside one and a link in a
   * card is still a tab stop of its own: focus reaching it has to move the board's
   * position, or the board keeps its tab stop on the card the reader has left and the
   * next arrow key moves from there. False for a cell, whose cards are positions in their
   * own right and report themselves.
   */
  fromDescendants?: boolean
}

export type RovingFocusResult = {
  /**
   * Whether this element is the one the position names. Exactly one element on the board
   * carries `tabIndex: 0`, and this is how a caller knows it is the one, so that each can
   * state what it is at rest: a card stays reachable by pointer, a cell body is not
   * focusable at all until the position is on it.
   */
  isRovingItem: boolean
  onFocus: FocusEventHandler<HTMLElement>
}

/**
 * Makes one element the board's roving item while the focus position names it.
 *
 * The element is told to take focus when the position was moved and nothing has taken it
 * yet. An element that is not in the page cannot, and that is the case worth stating: the
 * request stays outstanding, and the element takes focus when it renders. Today that
 * happens when a column scrolls back into the board's window; when a cell windows its own
 * cards it will happen for a card too, and nothing here has to change for it.
 *
 * `position` must be stable across renders, because it keys the subscription and the focus
 * report, and a fresh object every render would rebuild both every render.
 */
export function useRovingFocus(
  position: BoardFocusPosition,
  elementRef: RefObject<HTMLElement | null>,
  {fromDescendants = false}: RovingFocusOptions = {},
): RovingFocusResult {
  const {dispatch, getState, subscribe, claimPosition, reportFocusAtPosition} = useBoardFocusStore()
  const grid = useBoardFocusGrid()

  const getSnapshot = useCallback(() => {
    const {position: current, pendingFocus} = getState()
    if (!isSamePosition(position, current)) return NOT_ROVING

    return pendingFocus ? ROVING_AND_OWED_FOCUS : ROVING
  }, [getState, position])

  // The same snapshot on the server: it is read from the reducer's seeded state, which is
  // derived from the caller's data and so is the same on both sides.
  const roving = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const isRovingItem = roving !== NOT_ROVING

  // Tells the board that the position has an element behind it, which is what lets the
  // root give up being a tab stop. Without it, a position naming a column the window has
  // scrolled past would leave the board with no way in from the keyboard at all.
  useEffect(() => (isRovingItem ? claimPosition() : undefined), [isRovingItem, claimPosition])

  /*
    Focus can already be here by the time there is anything to hear it. This board ships
    its tab stop in the server's markup deliberately, so that a reader can tab into it
    before it hydrates, and focus that landed before React attached its listeners fires no
    event they will ever see.

    Nothing else would notice. The board would hold a position with nobody standing on it:
    the arrow keys declined, because no element reported taking focus, and the caller told
    nobody is on the card the reader is looking at.

    Once, on mount, because an element that is not in the page cannot already hold focus.
  */
  const observeFocusAlreadyHere = useEffectEvent(() => {
    const element = elementRef.current
    if (element === null) return

    const focused = element.ownerDocument.activeElement
    const isHere = fromDescendants ? element.contains(focused) : focused === element
    if (!isHere) return

    dispatch({type: 'observe', grid, position})
  })

  useEffect(() => {
    observeFocusAlreadyHere()
  }, [])

  // A layout effect, so focus never visibly falls to the document between the element
  // that had it being removed and this one taking it.
  useLayoutEffect(() => {
    if (roving !== ROVING_AND_OWED_FOCUS) return

    const element = elementRef.current
    if (element === null) return

    element.focus()

    // Focusing an element that already has focus fires no event, so the request would
    // stay outstanding for a move that clamped at an edge. Reported here instead, and
    // only once focus really is here, because an element that refused it is still owed.
    if (element.ownerDocument.activeElement === element) {
      dispatch({type: 'observe', grid, position})
    }
  }, [roving, elementRef, dispatch, grid, position])

  const onFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      // The event is `focusin` underneath, so it arrives at every roving element between
      // the one focus landed on and the board's root, innermost first. A cell takes only
      // its own, or a card taking focus would be overruled by the cell around it a moment
      // later. A card takes its subtree, because everything in it belongs to it.
      const isHere = fromDescendants ? event.currentTarget.contains(event.target) : event.target === event.currentTarget
      if (!isHere) return

      // Told to the board's root, which hears this same event as it carries on up and
      // otherwise has no way to tell focus at a position from focus on a control the
      // caller drew between them.
      reportFocusAtPosition(event.nativeEvent)
      dispatch({type: 'observe', grid, position})
    },
    [dispatch, grid, position, fromDescendants, reportFocusAtPosition],
  )

  return {isRovingItem, onFocus}
}
